import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { TOUR_STORAGE_KEY } from "../components/OnboardingTour";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Drives the OnboardingTour visibility with a two-tier persistence model:
 *   1. Backend flag `user.onboarding_completed_at` — survives localStorage
 *      wipes, incognito sessions and multi-device use.
 *   2. localStorage `alp.tour.completed.v1` — instant optimistic check before
 *      the `/auth/me` roundtrip resolves, so the tour doesn't flash open.
 *
 * Exposes startTour() for manual re-open (e.g., a "Restart tour" entry).
 */
export function useOnboardingTour() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  // Initial auto-open: only if BOTH localStorage and user profile say "not done".
  useEffect(() => {
    // Iter54 — never run the tour on PUBLIC pages (Landing, public footer pages, OAuth callback).
    const PUBLIC_ROUTES = ["/", "/landing", "/login", "/signup", "/auth/callback",
      "/about", "/contact", "/privacy", "/terms", "/cookies", "/careers"];
    if (PUBLIC_ROUTES.includes(location.pathname)) return undefined;
    // Wait until we know who the user is (user===null means still loading).
    if (user === null) return undefined;
    // Iter48 — never run the client onboarding tour on the Master Console.
    if ((location.pathname || "").startsWith("/master")) return undefined;
    // Iter48 — and not on the forced password-change page either.
    if (location.pathname === "/change-password") return undefined;
    // Tour only makes sense for authenticated users.
    if (!user) return undefined;

    let localDone = false;
    try { localDone = window.localStorage.getItem(TOUR_STORAGE_KEY) === "1"; } catch (_e) { /* ignore */ }

    const serverDone = !!(user && user.onboarding_completed_at);
    // If server already knows — mirror to local so we don't re-check next time.
    if (serverDone && !localDone) {
      try { window.localStorage.setItem(TOUR_STORAGE_KEY, "1"); } catch (_e) { /* ignore */ }
    }
    if (localDone || serverDone) return undefined;

    const tid = window.setTimeout(() => setOpen(true), 800);
    return () => window.clearTimeout(tid);
  }, [user]);

  // Defense-in-depth: on every route change, if dismissal is known, close.
  useEffect(() => {
    let localDone = false;
    try { localDone = window.localStorage.getItem(TOUR_STORAGE_KEY) === "1"; } catch (_e) { /* ignore */ }
    const serverDone = !!(user && user.onboarding_completed_at);
    if (localDone || serverDone) setOpen(false);
  }, [location.pathname, user]);

  const startTour = useCallback(() => {
    try { window.localStorage.removeItem(TOUR_STORAGE_KEY); } catch (_e) { /* ignore */ }
    // Fire-and-forget: reset server flag too so the next load doesn't auto-close.
    axios.post(`${API}/user/onboarding-reset`, {}).catch(() => {});
    setOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    // Persist locally first (instant) + sync to server (durable).
    try { window.localStorage.setItem(TOUR_STORAGE_KEY, "1"); } catch (_e) { /* ignore */ }
    axios.post(`${API}/user/onboarding-dismiss`, {}).catch(() => {});
    setOpen(false);
  }, []);

  return { open, startTour, closeTour };
}
